import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Icon from '../components/ui/Icon';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { EmptyState, ErrorState } from '../components/ui/States';
import UsersTable from '../components/users/UsersTable';
import useUsers from '../components/users/useUsers';
import { DEFAULT_PARAMS, LIMIT_OPTIONS, nextSort } from '../components/users/usersParams';
import { usersApi } from '../api/users';
import { getMessage } from '../api/client';
import { canSuspendUsers } from '../auth/permissions';

const fmt = (n) => n.toLocaleString('en-US');

function errorView(error) {
  switch (error?.status) {
    case 403:
      return { icon: 'lock', title: "You don't have permission to view users", retry: false };
    case 429:
      return { icon: 'hourglass_top', title: 'Too many requests, try again shortly', retry: true, retryAfter: 5 };
    default:
      return { icon: 'cloud_off', title: "Couldn't load users", description: error?.message, retry: true };
  }
}

export default function UsersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const searchRef = useRef(null);
  const {
    params,
    setParams,
    searchInput,
    setSearchInput,
    clearSearch,
    rows,
    pagination,
    totalRegistered,
    isInitialLoading,
    isRefreshing,
    error,
    refetch,
  } = useUsers();

  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspending, setSuspending] = useState(false);

  // ⌘K / Ctrl+K focuses the search box
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const showSuspend = canSuspendUsers();
  const getActions = (user) => {
    const id = user.userId;
    const items = [
      { label: 'View Profile', disabled: !id, onSelect: () => navigate(`/users/${encodeURIComponent(id)}`, { state: { from: location.search } }) },
    ];
    if (showSuspend) {
      items.push({
        label: 'Suspend User',
        danger: true,
        disabled: !id || user.status === 'inactive',
        hint: user.status === 'inactive' ? 'This user is already inactive' : undefined,
        onSelect: () => setSuspendTarget(user),
      });
    }
    return items;
  };

  const refreshAfterSuspend = (userId) => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
    queryClient.invalidateQueries({ queryKey: ['user', userId] });
  };

  const confirmSuspend = async () => {
    const { userId, name } = suspendTarget;
    setSuspending(true);
    try {
      await usersApi.suspend(userId);
      toast.success(`${name} has been suspended`);
      setSuspendTarget(null);
      refreshAfterSuspend(userId);
    } catch (err) {
      toast.error(getMessage(err?.data) || 'Something went wrong. Please try again');
      // 290: not found or already inactive, so the row on screen is out of date
      if (err?.status === 290) refreshAfterSuspend(userId);
    } finally {
      setSuspending(false);
    }
  };

  const hasFilters = params.status !== DEFAULT_PARAMS.status || params.fuelType !== DEFAULT_PARAMS.fuelType;
  const clearFilters = () => clearSearch({ status: DEFAULT_PARAMS.status, fuelType: DEFAULT_PARAMS.fuelType });
  const total = pagination?.totalItems ?? 0;
  const showEmpty = !error && !isInitialLoading && rows.length === 0 && pagination;
  const announcement =
    isInitialLoading || isRefreshing || !pagination
      ? ''
      : `${fmt(total)} ${total === 1 ? 'user' : 'users'}${params.search ? ` matching "${params.search}"` : ''}`;

  let body;
  if (error && !isInitialLoading) {
    const view = errorView(error);
    body = (
      <ErrorState
        icon={view.icon}
        title={view.title}
        description={view.description}
        retryAfter={view.retryAfter}
        onRetry={view.retry ? () => refetch() : undefined}
      />
    );
  } else if (showEmpty) {
    body =
      params.search || hasFilters ? (
        <EmptyState
          tone="neutral"
          icon="person_search"
          title={params.search ? `No users match '${params.search}'` : 'No users match these filters'}
          action={
            <Button variant="secondary" icon="close" onClick={clearFilters}>
              {params.search ? 'Clear search' : 'Clear filters'}
            </Button>
          }
        />
      ) : (
        <EmptyState tone="neutral" icon="group" title="No users yet" description="Registered drivers will appear here." />
      );
  } else {
    body = (
      <UsersTable
        rows={rows}
        params={params}
        onSort={(field) => setParams(nextSort(params, field))}
        getActions={getActions}
        loading={isInitialLoading}
        refreshing={isRefreshing}
        skeletonCount={Math.min(params.limit, 10)}
      />
    );
  }

  return (
    <>
      <section className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-on-surface tracking-tight">Users</h1>
          {totalRegistered != null && (
            <span className="px-2.5 py-0.5 rounded-full bg-surface-container-high text-on-surface-variant font-data-mono text-[11px]">
              {fmt(totalRegistered)} Registered
            </span>
          )}
        </div>

        <div className="relative w-full lg:w-80" role="search">
          <label htmlFor="users-search" className="sr-only">
            Search users
          </label>
          <Icon
            name="search"
            size={18}
            className="text-on-surface-variant absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          />
          <input
            ref={searchRef}
            id="users-search"
            type="search"
            autoComplete="off"
            maxLength={100}
            placeholder="Search by name, email, phone"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && searchInput && clearSearch()}
            aria-describedby="users-search-hint"
            className="w-full h-9 pl-9 pr-12 text-[13px] bg-surface-container-lowest border border-outline-variant/50 rounded-xl shadow-xs text-on-surface placeholder:text-on-surface-variant/60 outline-none transition focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]"
          />
          <span id="users-search-hint" className="sr-only">
            Type at least 2 characters. Press Escape to clear.
          </span>
          <kbd className="hidden sm:block absolute right-2 top-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded bg-surface-container text-[10px] font-data-mono text-on-surface-variant font-medium">
            ⌘K
          </kbd>
        </div>
      </section>

      <section className="relative bg-surface-container-lowest rounded-2xl border border-outline-variant/30 shadow-xs overflow-hidden">
        <p className="sr-only" role="status" aria-live="polite">
          {announcement}
        </p>
        {isRefreshing && (
          <div className="absolute top-2 right-3 z-30 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-surface-container-lowest/90 shadow-xs text-[11px] text-on-surface-variant">
            <Icon name="progress_activity" size={14} className="animate-spin text-secondary" />
            Loading…
          </div>
        )}

        {body}

        {!error && pagination && total > 0 && (
          <Pagination
            page={params.page}
            pageSize={params.limit}
            total={total}
            noun={total === 1 ? 'user' : 'users'}
            onPageChange={(page) => setParams({ page })}
            pageSizeOptions={LIMIT_OPTIONS}
            onPageSizeChange={(limit) => setParams({ limit })}
            disabled={isRefreshing}
          />
        )}
      </section>

      <ConfirmDialog
        open={Boolean(suspendTarget)}
        title="Suspend user?"
        description={`${suspendTarget?.name} (${suspendTarget?.userId}) will no longer be able to log in to the Pompi app. Their data will be kept.`}
        confirmLabel="Suspend User"
        busy={suspending}
        onConfirm={confirmSuspend}
        onCancel={() => setSuspendTarget(null)}
      />
    </>
  );
}
